import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { signIn } from "actions/helpers/auth";
import PageSpinner from "components/Spinners/PageSpinner";
import { Button } from "components/Theme/button";
import { Form, Formik, type FormikHelpers } from "formik";
import { primeAndScopeProfile } from "hooks/useProfile";
import AuthField from "layouts/AuthField";
import type React from "react";
import { useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "reducers/auth";
import { useReferer } from "reducers/referer";
import * as Yup from "yup";
import LoginErrorBanner from "./LoginErrorBanner";

interface FormValues {
  email: string;
  password: string;
}

export default function Login(): React.ReactElement {
  const navigate = useNavigate();
  const referer = useReferer();

  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { dispatch } = authContext;

  // biome-ignore lint/correctness/useExhaustiveDependencies(navigate): shouldnt change?
  const handleSuccessfulAuthentication = useCallback(() => {
    if (
      referer &&
      referer.pathname !== "/" &&
      referer.pathname !== "/auth/reset-password"
    ) {
      navigate(referer);
    } else {
      navigate("/admin");
    }
  }, [referer]);

  // Session restore now happens in <AuthBootstrap> above the router, so an
  // already-authenticated visit to /auth/login redirects straight to the app
  // rather than showing the form.
  // biome-ignore lint/correctness/useExhaustiveDependencies(handleSuccessfulAuthentication): stable
  useEffect(() => {
    if (authContext.state.authenticated) {
      handleSuccessfulAuthentication();
    }
  }, [authContext.state.authenticated]);

  const submit = (
    { email, password }: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ): void => {
    setSubmitting(true);
    dispatch({ type: "AUTHENTICATING" });
    Sentry.setUser({ email });
    // One chain end-to-end: a failed profile load after sign-in still reaches the
    // catch (it isn't a floating inner promise). The redirect on success is owned
    // by the `authenticated` effect above, so there is no second navigate here.
    // primeAndScopeProfile also drops a previous user's persisted cache before
    // the app is shown (shared-browser case), re-priming if it cleared.
    signIn(email, password)
      .then(() => primeAndScopeProfile(queryClient))
      .then(() => dispatch({ type: "AUTHENTICATING_SUCCESS" }))
      .catch((err: Error) => {
        dispatch({ type: "AUTHENTICATING_ERROR", payload: err });
        setSubmitting(false);
      });
  };

  const initialValues = {
    email: "",
    password: "",
  };

  return (
    <PageSpinner spin={authContext.state.authenticating}>
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={Yup.object().shape({
          email: Yup.string().required("Required."),
          password: Yup.string().required("Required."),
        })}
      >
        {({ isValid, isSubmitting }): React.ReactElement => (
          <Form>
            <AuthField name={"email"} label={"Email"} type={"text"} />
            <AuthField name={"password"} label={"Password"} type={"password"} />
            <div className={"flex py-6 justify-end gap-4"}>
              <div className={"my-auto"}>
                <Button
                  outline={true}
                  bright={true}
                  paddingX={4}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    navigate("/auth/reset-password");
                  }}
                >
                  Forgotten password?
                </Button>
              </div>
              <div>
                <Button
                  color="brandLight"
                  disabled={!isValid || isSubmitting}
                  type="submit"
                  paddingX={4}
                >
                  Login
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <LoginErrorBanner authError={authContext.state.authError} />
    </PageSpinner>
  );
}
